import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Boxes } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, Kpi } from "@/components/common";
import { inr, num } from "@/lib/format";

const COLORS = ["#2563eb", "#f59e0b", "#8b5cf6", "#06b6d4", "#10b981"];

export default function ProductEconomics() {
  const { data } = usePoll(endpoints.products, 6000);
  if (!data) return <Loading />;
  const t = data.totals;

  return (
    <div data-testid="product-economics-page">
      <PageHeader icon={Boxes} title="Product Economics"
        subtitle="True utility cost & contribution margin per product — ₹ utility/kg and margin across the portfolio." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi testid="prod-margin-day" label="Daily Margin" value={inr(t.margin_day)} accent="#10b981" />
        <Kpi testid="prod-revenue-day" label="Daily Revenue" value={inr(t.revenue_day)} accent="#2563eb" />
        <Kpi testid="prod-best" label="Best Margin" value={t.best_margin} accent="#7c3aed" />
        <Kpi testid="prod-intensive" label="Most Utility-Intensive" value={t.most_utility_intensive} accent="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
        <Panel testid="panel-utility-per-unit" title="Utility Cost per Unit" subtitle="₹ utility / kg (or L)">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.products} margin={{ left: 0, right: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => "₹" + v} />
              <Bar dataKey="utility_per_unit" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {data.products.map((p, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel testid="panel-margin-per-unit" title="Contribution Margin per Unit" subtitle="₹ margin / kg (or L)">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.products} margin={{ left: 0, right: 10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => "₹" + v} />
              <Bar dataKey="margin_per_unit" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {data.products.map((p, i) => <Cell key={i} fill={p.margin_per_unit < 0 ? "#ef4444" : "#10b981"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel testid="panel-product-table" title="Product P&L" subtitle="Per-unit economics with utility cost broken out">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="text-left font-semibold py-2">Product</th>
              <th className="text-right font-semibold">Milk/Unit</th>
              <th className="text-right font-semibold">Sell ₹</th>
              <th className="text-right font-semibold">Utility ₹</th>
              <th className="text-right font-semibold">Margin ₹</th>
              <th className="text-right font-semibold">Margin %</th>
              <th className="text-right font-semibold">Units/Day</th>
              <th className="text-right font-semibold">Margin/Day</th>
            </tr>
          </thead>
          <tbody>
            {data.products.map((p, i) => (
              <tr key={p.id} className="border-b border-slate-50" data-testid={`product-row-${p.id}`}>
                <td className="py-2.5 font-medium text-slate-800 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                  {p.name} <span className="text-[10px] text-slate-400">/{p.unit}</span>
                </td>
                <td className="text-right font-mono text-slate-500">{p.milk_per_unit} L</td>
                <td className="text-right font-mono">{num(p.sell)}</td>
                <td className="text-right font-mono text-amber-600">{num(p.utility_per_unit, 1)}</td>
                <td className={`text-right font-mono font-semibold ${p.margin_per_unit < 0 ? "text-red-600" : "text-emerald-600"}`}>{num(p.margin_per_unit, 1)}</td>
                <td className="text-right font-mono text-slate-500">{p.margin_pct}%</td>
                <td className="text-right font-mono text-slate-500">{num(p.units_per_day)}</td>
                <td className="text-right font-mono font-semibold text-slate-900">{inr(p.margin_day)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
